import axios from 'axios';
import { LatestClosedPeriod, ReportPreflightResult, ReportRequest, ReportStatus, ReportTarget } from '../types/api';
import { clearFrontendSessionState } from '../utils/sessionCleanup';

interface CostCenter {
  Id: string;
  Name: string;
  Number?: number;
  IsActive?: boolean;
  Items?: Array<{
    Id: string;
    Name: string;
    ShortName: string;
    IsActive: boolean;
    CreatedUtc: string;
    CostCenterId: string;
  }>;
}

const inferLocalFallback = (): string => {
  if (typeof window === 'undefined') {
    return 'https://localhost:3000/api';
  }

  const { protocol, hostname, port } = window.location;

  // CRA dev server defaults to localhost:3001 (see package.json). Backend TLS runs on 3000.
  if (hostname === 'localhost' && port === '3001') {
    return '/api';
  }

  // If frontend already on same origin (e.g. docker compose), reuse it.
  if (port === '3000') {
    return `${protocol}//${hostname}/api`;
  }

  return `${protocol}//${hostname}/api`;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || inferLocalFallback();
export const SERVICE_UNAVAILABLE_MESSAGE = 'Tjänsten är tillfället otillgänglig (503). Försök igen om en liten stund.';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const isSilentSessionRecoveryRequest = (requestUrl: string) => {
  return [
    '/auth/oidc/authorize',
    '/auth/me',
    '/auth/profile',
  ].some((path) => requestUrl.includes(path));
};

const redirectToLogin = (message?: string) => {
  clearFrontendSessionState(message ? { authError: message } : undefined);

  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const parseFileNameFromDisposition = (header?: string): string | null => {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const simpleMatch = header.match(/filename="?([^";]+)"?/i);
  return simpleMatch?.[1] ?? null;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetriableApiError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }

  if (!error.response) {
    return true;
  }

  return error.code === 'ECONNABORTED';
};

const withRetry = async <T>(operation: () => Promise<T>, maxAttempts = 3): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt >= maxAttempts || !isRetriableApiError(error)) {
        break;
      }
      await sleep(200 * attempt);
    }
  }

  throw lastError;
};

// Add interceptor to handle 401 (session expired)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl: string = error?.config?.url || '';

    if (status === 503 && isSilentSessionRecoveryRequest(requestUrl)) {
      error.message = SERVICE_UNAVAILABLE_MESSAGE;
      (error as any).userMessage = SERVICE_UNAVAILABLE_MESSAGE;
      (error as any).suppressUserError = true;
      return Promise.reject(error);
    }

    if (status === 503) {
      error.message = SERVICE_UNAVAILABLE_MESSAGE;
      (error as any).userMessage = SERVICE_UNAVAILABLE_MESSAGE;
    }

    if (status === 401) {
      const isAuthStatus = requestUrl.includes('/auth/status');

      // Avoid redirect loops on endpoints that are intentionally called while unauthenticated.
      if (!isAuthStatus) {
        redirectToLogin('Sessionen har gått ut. Logga in igen.');
      }
    }
    return Promise.reject(error);
  }
);

export type AdminSpirisProfileStatus = {
  profileKey: string;
  status: string;
  updatedAt?: string;
  lastError?: string;
  connectedBy?: string;
  connectedAt?: string;
  refreshTokenIssuedAt?: string;
  refreshTokenExpiresAt?: string;
  expectedOrgNrSet?: boolean;
  expectedOrgNr?: string;
  connectPath?: string;
  connectUrl?: string;
};

export type SessionInfo = {
  authenticated: boolean;
  success?: boolean;
  error?: string;
  profileKey?: string;
  target?: ReportTarget;
  allAccess?: boolean;
  isAdmin?: boolean;
  email?: string;
  name?: string;
  tokenStatus?: string;
  refreshTokenExpiresAt?: string;
  refreshTokenExpired?: boolean;
};

type AdminAuthSettings = {
  allAccessGroup?: string;
  updatedAt?: string;
  updatedBy?: string;
  source?: string;
};

export const reportAPI = {
  // Health check
  health: async (): Promise<{ status: string }> => {
    const response = await apiClient.get('/health');
    return response.data;
  },

  // Authentication
  getAuthorizationUrl: async (loginHint?: string, returnTo?: string, forceOidc?: boolean): Promise<{ authUrl: string; state: string }> => {
    const params = new URLSearchParams();
    if (loginHint) params.set('loginHint', loginHint);
    if (returnTo) params.set('returnTo', returnTo);
    if (forceOidc) params.set('force', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get(`/auth/oidc/authorize${qs}`);
    return response.data;
  },

  getAuthStatus: async (): Promise<{ authenticated: boolean }> => {
    const response = await apiClient.get('/auth/status');
    return response.data;
  },

  getSessionInfo: async (): Promise<SessionInfo> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  logout: async (): Promise<{ success: boolean }> => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  // Admin profile management
  listAdminProfiles: async (): Promise<{ profiles: Array<{ profileKey: string; status: string; connectedBy?: string; connectedAt?: string; updatedAt?: string; lastError?: string; refreshTokenIssuedAt?: string; refreshTokenExpiresAt?: string }>; count: number }> => {
    const response = await apiClient.get('/admin/spiris/profiles');
    return response.data;
  },

  createProfile: async (profileKey: string): Promise<{ profileKey: string; status: string }> => {
    const response = await apiClient.post('/admin/spiris/profiles', { profileKey });
    return response.data;
  },

  deleteProfile: async (profileKey: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/admin/spiris/profiles/${encodeURIComponent(profileKey)}`);
    return response.data;
  },

  reconnectProfile: async (profileKey: string): Promise<void> => {
    // This redirects to Visma, so we set localStorage flag and redirect manually
    localStorage.setItem('post_spiris_connect_page', 'admin');
    const base = API_BASE_URL.replace(/\/$/, '');
    window.location.href = `${base}/admin/spiris/profiles/${encodeURIComponent(profileKey)}/reconnect`;
  },

  // Admin auth settings
  getAdminAuthSettings: async (): Promise<AdminAuthSettings> => {
    const response = await apiClient.get('/admin/settings/auth');
    return response.data;
  },

  updateAdminAuthSettings: async (allAccessGroup: string): Promise<AdminAuthSettings> => {
    const response = await apiClient.post('/admin/settings/auth', { allAccessGroup });
    return response.data;
  },

  // Profile selection
  listProfiles: async (): Promise<{ profiles: string[] }> => {
    const response = await apiClient.get('/auth/profiles');
    return response.data;
  },

  listTargets: async (): Promise<{ targets: ReportTarget[] }> => {
    const response = await apiClient.get('/auth/targets');
    return response.data;
  },

  getTarget: async (): Promise<{ target?: ReportTarget | null }> => {
    const response = await apiClient.get('/auth/target');
    return response.data;
  },

  setTarget: async (target: { id: string; type: ReportTarget['type'] }): Promise<{ target: ReportTarget }> => {
    const response = await apiClient.post('/auth/target', target);
    return response.data;
  },

  getProfileKey: async (): Promise<{ profileKey: string }> => {
    const response = await apiClient.get('/auth/profile');
    return response.data;
  },

  setProfileKey: async (profileKey: string): Promise<{ profileKey: string }> => {
    const response = await apiClient.post('/auth/profile', { profileKey });
    return response.data;
  },

  // Company/Reports endpoints
  getCostCenters: async (): Promise<CostCenter[]> => {
    const response = await withRetry(() => apiClient.get('/reports/cost-centers'));
    return response.data.data || response.data.costCenters || response.data || [];
  },

  getCompanySettings: async (): Promise<any> => {
    const response = await withRetry(() => apiClient.get('/reports/company-settings'));
    return response.data.data || response.data.companySettings || response.data || {};
  },

  getLatestClosedPeriod: async (): Promise<LatestClosedPeriod | null> => {
    try {
      const response = await withRetry(() => apiClient.get('/reports/latest-closed-period'));
      return response.data.data || null;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const backendError = (error.response?.data as { error?: string } | undefined)?.error;
        if (typeof backendError === 'string' && backendError.trim() !== '') {
          throw new Error(backendError);
        }
      }
      throw new Error('Kunde inte hämta senaste stängda period just nu.');
    }
  },

  generateReport: async (request: ReportRequest): Promise<{ reportId: string }> => {
    const response = await apiClient.post('/reports/generate', request);
    return response.data;
  },

  preflightReport: async (request: ReportRequest): Promise<ReportPreflightResult> => {
    const response = await apiClient.post('/reports/preflight', request);
    return response.data;
  },

  getReportStatus: async (reportId: string, signal?: AbortSignal): Promise<ReportStatus> => {
    const response = await apiClient.get(`/reports/status/${reportId}`, { signal });
    return response.data;
  },

  downloadReportFile: async (reportId: string, signal?: AbortSignal): Promise<{ blob: Blob; fileName: string | null }> => {
    const response = await apiClient.get(`/reports/files/${reportId}`, {
      responseType: 'blob',
      signal,
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const fileName = parseFileNameFromDisposition(disposition);
    return { blob: response.data, fileName };
  },
};

